import { Component, Input, OnInit, ViewChild, OnDestroy, OnChanges } from '@angular/core';
import { CoachingThresholdComponent } from '@app-asset-config/components/coaching-threshold/coaching-threshold.component';
import {
  CoachingConfig,
  configEvents,
  GetCoachingConfigResponse,
  PostCoachingConfigResponse,
} from '@app-asset-config/models/tagging.model';
import { TaggingService } from '@app-asset-config/services/tagging.service';
import { StorageService } from '@app-core/services/storage/storage.service';
import { EVENTS_CONFIG, EXCLUDED_EVENTS, VIDEO_TYPE_LIST } from '@app-core/constants/constants';
import { DataService } from '@app-core/services/data/data.service';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { GoogleTagManagerService } from '@app-core/services/google-tag-manager/google-tag-manager.service';
import { TranslateService } from '@ngx-translate/core';
import {
  DEFAULT_INCIDENTS_PER_100_MILES,
  DEFAULT_COACHING_INTERVAL,
  MINIMUM_EVENT_THREASHOLD,
} from '@app-asset-config/constants/config.constants';
import { MatDialog } from '@angular/material/dialog';
import { CoachingSelectionModalComponent } from '@app-shared/components/coaching-selection-modal/coaching-selection-modal.component';
import * as moment from 'moment';
import { PreferencesResponse } from '@app-core/models/core.model';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
import { AutoCoachingEventSelectionComponent } from '@app-asset-config/components/auto-coaching-event-selection/auto-coaching-event-selection.component';
import { InPersonEscalationComponent } from '@app-asset-config/components/in-person-escalation/in-person-escalation.component';

@Component({
  selector: 'app-coaching-config-tab',
  templateUrl: './coaching-config-tab.component.html',
  styleUrls: ['./coaching-config-tab.component.scss'],
})
export class CoachingConfigTabComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public fleetId: string;
  @Input() public isOpenedInSideNav: boolean = false;
  @Input() activeTabIndex: number = 2;

  @ViewChild(CoachingThresholdComponent)
  coachingThreshold: CoachingThresholdComponent;

  @ViewChild(AutoCoachingEventSelectionComponent)
  autoCoachingEventSelection: AutoCoachingEventSelectionComponent;

  @ViewChild(InPersonEscalationComponent)
  inPersonEscalation: InPersonEscalationComponent;

  public coachConfigLoader: boolean = true;
  public eventTypes: string[];
  public escalationEventTypes: string[];
  public incidentsPer100Miles: number = DEFAULT_INCIDENTS_PER_100_MILES;
  public coachingInterval: number = DEFAULT_COACHING_INTERVAL;
  public minimumEventThreshold: number = MINIMUM_EVENT_THREASHOLD;
  public loader: boolean = false;
  public enableAutoSelection: boolean;
  public maxAutoSelectedEvents: number;
  public autoCoachingLoader: boolean = false;
  public enableSelfCoaching: boolean;
  public enableInPersonEscalation: boolean;
  public missedSelfCoachingSessions: number;
  public minConsecutiveSelfCoachingSessions: number;
  public inPersonEscalationLoader: boolean = false;
  public coachingSwitchCountdown: number;
  public showCountDown: boolean = false;
  public eventsConfig: configEvents[];
  public othersConfig: configEvents[];
  public customEvents: configEvents[];
  public isSelfCoachingFeatureFlagEnabled: boolean = false;
  private previousCoachingMode: boolean;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private taggingService: TaggingService,
    private storageService: StorageService,
    public dataService: DataService,
    private snachBarService: SnackBarService,
    private gtmService: GoogleTagManagerService,
    public translate: TranslateService,
    private dialog: MatDialog,
    private mixpanelService: MixPanelService
  ) {}

  ngOnInit(): void {
    this.mixpanelService.track('screen-viewed/configurations-page/coaching-page/main-content/coaching');
    this.getFleetDetails();
    const combinedEventsList = this.dataService.modifyFleeEvents();
    const modifiedEventsConfig = this.dataService.transformObject(combinedEventsList);
    const filteredCustomData = {};
    const filterStandardEvents = {};
    for (const key in modifiedEventsConfig) {
      if (!(key in EVENTS_CONFIG)) {
        filteredCustomData[key] = modifiedEventsConfig[key];
      } else {
        filterStandardEvents[key] = modifiedEventsConfig[key];
      }
    }
    const otherConfigList = VIDEO_TYPE_LIST.filter((obj) => obj.label !== 'Event On-Demand');
    this.othersConfig = this.processConfig(otherConfigList);
    this.eventsConfig = this.processConfig(filterStandardEvents);
    this.customEvents = this.processConfig(filteredCustomData);
    // this.getCoachingConfig();
  }

  ngOnChanges(): void {
    this.getCoachingConfig();
  }

  public getFleetDetails() {
    this.dataService
      .getFleetDetails()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res: PreferencesResponse) => {
          if (res?.driverSelfCoachingConfiguration?.isEnabled) {
            this.isSelfCoachingFeatureFlagEnabled = true;
          } else {
            this.isSelfCoachingFeatureFlagEnabled = false;
          }
        },
        error: () => {},
      });
  }

  private processConfig(configObject): configEvents[] {
    return Object.entries(configObject)
      .map(([key, value]: [string, configEvents]) => ({
        key,
        checked: false,
        ...value,
      }))
      .sort((a, b) => (a.label > b.label ? 1 : -1));
  }

  get hasUnsavedChanges(): boolean {
    const thresholdChanges = this.coachingThreshold?.isEventsTypeValid === false;
    const autoCoachingChanges = this.autoCoachingEventSelection?.valuesChanged ?? false;
    const inPersonEscalationChanges =
      this.enableSelfCoaching && (this.inPersonEscalation?.valuesChanged || this.inPersonEscalation?.isEventsTypeValid === false);
    return thresholdChanges || autoCoachingChanges || inPersonEscalationChanges;
  }

  private getCoachingConfig() {
    this.coachConfigLoader = true;
    this.storageService.setStorageValue('coachingConfig', {});
    const params = {
      fleetId: this.fleetId,
    };
    this.taggingService
      .getFleetCoachConfig(params, true)
      .pipe(
        finalize(() => {
          this.coachConfigLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: GetCoachingConfigResponse) => {
          const {
            eventTypes = Object.keys(EVENTS_CONFIG),
            escalationEventTypes = Object.keys(EVENTS_CONFIG),
            incidentsPer100Miles = DEFAULT_INCIDENTS_PER_100_MILES,
            coachingInterval = DEFAULT_COACHING_INTERVAL,
            minimumEventThreshold = MINIMUM_EVENT_THREASHOLD,
            enableAutoSelection,
            enableSelfCoaching,
            maxAutoSelectedEvents,
            enableInPersonEscalation,
            missedSelfCoachingSessions,
            minConsecutiveSelfCoachingSessions,
          } = res?.value?.coachingConfig || {};

          const filteredEventTypes = eventTypes?.filter((event) => !EXCLUDED_EVENTS.includes(event)) ?? [];
          const filteredEscalationEventTypes = escalationEventTypes?.filter((event) => !EXCLUDED_EVENTS.includes(event)) ?? [];

          this.eventTypes = filteredEventTypes;
          this.escalationEventTypes = filteredEscalationEventTypes;
          this.incidentsPer100Miles = incidentsPer100Miles;
          this.coachingInterval = coachingInterval;
          this.minimumEventThreshold = minimumEventThreshold;
          this.enableAutoSelection = enableAutoSelection;
          this.maxAutoSelectedEvents = maxAutoSelectedEvents;
          this.enableSelfCoaching = enableSelfCoaching || false;
          this.enableInPersonEscalation = enableInPersonEscalation || false;
          this.missedSelfCoachingSessions = missedSelfCoachingSessions;
          this.minConsecutiveSelfCoachingSessions = minConsecutiveSelfCoachingSessions;
          this.previousCoachingMode = enableSelfCoaching || false;

          this.storageService.setStorageValue('coachingConfig', {
            ...res?.value?.coachingConfig,
            eventTypes: filteredEventTypes,
            escalationEventTypes: filteredEscalationEventTypes,
          });

          const selfCoachingToggledAt = res?.value?.coachingConfig.selfCoachingToggledAt;
          this.updateCoachingCountdown(selfCoachingToggledAt);
        },
        () => {}
      );
  }

  public coachingThresholdsubmit(event: CoachingConfig) {
    this.loader = true;

    const updatedEvent: CoachingConfig = {
      ...event,
      enableSelfCoaching: this.enableSelfCoaching,
      enableInPersonEscalation: this.enableInPersonEscalation,
      enableAutoSelection: this.enableAutoSelection,
      maxAutoSelectedEvents: this.maxAutoSelectedEvents,
      missedSelfCoachingSessions: this.missedSelfCoachingSessions,
      minConsecutiveSelfCoachingSessions: this.minConsecutiveSelfCoachingSessions,
      escalationEventTypes: this.escalationEventTypes,
    };

    this.onSubmit(updatedEvent);
  }

  public autoCoachingEventsubmit(event: CoachingConfig) {
    this.autoCoachingLoader = true;

    const updatedEvent: CoachingConfig = {
      ...event,
      eventTypes: this.eventTypes,
      escalationEventTypes: this.escalationEventTypes,
      incidentsPer100Miles: this.incidentsPer100Miles,
      coachingInterval: this.coachingInterval,
      minimumEventThreshold: this.minimumEventThreshold,
      enableSelfCoaching: this.enableSelfCoaching,
      enableInPersonEscalation: this.enableInPersonEscalation,
      missedSelfCoachingSessions: this.missedSelfCoachingSessions,
      minConsecutiveSelfCoachingSessions: this.minConsecutiveSelfCoachingSessions,
    };

    this.onSubmit(updatedEvent);
  }

  public inPersonEscalationValuessubmit(event: CoachingConfig) {
    this.inPersonEscalationLoader = true;
    const updatedEvent: CoachingConfig = {
      ...event,
      eventTypes: this.eventTypes,
      incidentsPer100Miles: this.incidentsPer100Miles,
      coachingInterval: this.coachingInterval,
      minimumEventThreshold: this.minimumEventThreshold,
      enableAutoSelection: this.enableAutoSelection,
      maxAutoSelectedEvents: this.maxAutoSelectedEvents,
    };

    this.onSubmit(updatedEvent);
  }

  private onSubmit(event: CoachingConfig) {
    this.gtmService.coachingThresholdSubmit();
    this.taggingService
      .updateFleetCoachConfig(event)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => {
          this.taggingService.isUpdatingConfig.next(false);
          this.loader = false;
          this.autoCoachingLoader = false;
          this.inPersonEscalationLoader = false;
        })
      )
      .subscribe(
        (res: PostCoachingConfigResponse) => {
          const config = res?.value?.coachingConfigJson;

          const filteredEventTypes = (config.eventTypes || []).filter((event: string) => !EXCLUDED_EVENTS.includes(event));
          const filteredEscalationEventTypes = (config.escalationEventTypes || []).filter(
            (event: string) => !EXCLUDED_EVENTS.includes(event)
          );

          this.updateCoachingCountdown(config.selfCoachingToggledAt);

          this.enableSelfCoaching = config.enableSelfCoaching;
          this.enableAutoSelection = config.enableAutoSelection;
          this.enableInPersonEscalation = config.enableInPersonEscalation;
          this.maxAutoSelectedEvents = config.maxAutoSelectedEvents;
          this.missedSelfCoachingSessions = config.missedSelfCoachingSessions;
          this.minConsecutiveSelfCoachingSessions = config.minConsecutiveSelfCoachingSessions;

          this.eventTypes = filteredEventTypes;
          this.escalationEventTypes = filteredEscalationEventTypes;

          this.incidentsPer100Miles = config.incidentsPer100Miles ?? DEFAULT_INCIDENTS_PER_100_MILES;
          this.coachingInterval = config.coachingInterval ?? DEFAULT_COACHING_INTERVAL;
          this.minimumEventThreshold = config.minimumEventThreshold ?? MINIMUM_EVENT_THREASHOLD;

          this.storageService.setStorageValue('coachingConfig', {
            ...config,
            eventTypes: filteredEventTypes,
            escalationEventTypes: filteredEscalationEventTypes,
          });

          const coachingModeChanged = this.previousCoachingMode !== config.enableSelfCoaching;
          if (coachingModeChanged) {
            this.snachBarService.success(this.translate.instant('Coaching mode switched'));
            this.mixpanelService.track('snackbar-shown/configurations-page/coaching-page/footer/coaching-mode-switched');
          } else {
            this.snachBarService.success(this.translate.instant('dutyTypeConfigurationUpdateSuccess'));
          }
          this.previousCoachingMode = config.enableSelfCoaching;
        },
        () => {
          this.snachBarService.success(this.translate.instant('dutyTypeConfigurationUpdateFailed'));
        }
      );
  }

  public handleDeadClick(event: Event) {
    //pending
    this.mixpanelService.track('dead-clicked/configurations-page/coaching-page/self-coaching-card/locked');
    event.stopPropagation();
  }

  public openSelectionModal() {
    this.mixpanelService.track('button-clicked/configurations-page/coaching-page/header/switch-coaching-mode');
    const dialogRef = this.dialog.open(CoachingSelectionModalComponent, {
      panelClass: ['coaching-selection-modal', 'mobile-modal'],
      autoFocus: false,
      disableClose: false,
      backdropClass: this.dataService._currentTheme.value !== 'dark' ? 'custom-backdrop' : '',
      data: {
        enableSelfCoaching: this.enableSelfCoaching,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === undefined) return;

      const coachingConfig = this.storageService.getStorageValue('coachingConfig');
      const keys = this.getEvents(this.eventsConfig, this.othersConfig, this.customEvents);
      const selectedKeys = this.getSelectedKeys(coachingConfig?.eventTypes, keys);

      this.onSubmit({
        enableSelfCoaching: result,
        enableAutoSelection: result,
        enableInPersonEscalation: result,
        eventTypes: selectedKeys,
        coachingInterval: coachingConfig?.coachingInterval,
        incidentsPer100Miles: coachingConfig?.incidentsPer100Miles,
        minimumEventThreshold: coachingConfig?.minimumEventThreshold,
      });
    });
  }

  private updateCoachingCountdown(selfCoachingToggledAt: string | number) {
    if (!selfCoachingToggledAt) {
      this.showCountDown = false;
      this.coachingSwitchCountdown = 0;
      return;
    }

    const TOTAL_HOURS = 12;

    const lastUpdatedLocal = moment.utc(selfCoachingToggledAt).local();
    const nowLocal = moment();

    const diffHours = nowLocal.diff(lastUpdatedLocal, 'hours', true);
    const remainingHours = Math.max(0, TOTAL_HOURS - diffHours);

    if (remainingHours > 0) {
      this.showCountDown = true;
      this.coachingSwitchCountdown = Math.min(TOTAL_HOURS, Math.ceil(remainingHours));
    } else {
      this.showCountDown = false;
      this.coachingSwitchCountdown = 0;
    }
  }

  public getEvents(eventsConfigs: any, otherConfig: any, customevents: any): string[] {
    const standarkEventKeys = Object.entries(eventsConfigs).map(([_key, value]: [string, configEvents]) => value.key);
    const otherConfigKeys = Object.entries(otherConfig).map(([_key, value]: [string, configEvents]) =>
      value.key === '0' ? 'panic-button' : ''
    );
    const customEventsKeys = Object.entries(customevents).map(([_key, value]: [string, configEvents]) => value.key);
    return [...standarkEventKeys, ...otherConfigKeys, ...customEventsKeys];
  }

  private getSelectedKeys(storedArray: any[], autocompleted: any[]): string[] {
    const includedKeys: string[] = [];
    autocompleted.forEach((value) => {
      if (storedArray.includes(value)) {
        includedKeys.push(value);
      }
    });
    return includedKeys;
  }

  get canSave(): boolean {
    return (
      this.hasUnsavedChanges &&
      this.coachingThreshold?.thresholdForm?.valid &&
      this.autoCoachingEventSelection?.autoCoachingThresholdForm?.valid &&
      (!this.enableSelfCoaching || this.inPersonEscalation?.inPersonEscalationThresholdForm?.valid)
    );
  }

  public saveAllConfigurations(): void {
    const coachingConfig = this.storageService.getStorageValue('coachingConfig');
    const threshold = this.coachingThreshold;

    const selectedEvents = [
      ...(threshold?.eventsConfig || []).filter((e) => e.checked).map((e) => (e.key === '0' ? 'panic-button' : e.key)),
      ...(threshold?.othersConfig || []).filter((e) => e.checked).map((e) => (e.key === '0' ? 'panic-button' : e.key)),
      ...(threshold?.customEvents || []).filter((e) => e.checked).map((e) => e.key),
    ];
    const auto = this.autoCoachingEventSelection;
    const escalation = this.inPersonEscalation;
    const escalationEvents = escalation?.enableInPersonEscalation
      ? [
          ...(escalation?.eventsConfig || []).filter((e) => e.checked).map((e) => (e.key === '0' ? 'panic-button' : e.key)),
          ...(escalation?.othersConfig || []).filter((e) => e.checked).map((e) => (e.key === '0' ? 'panic-button' : e.key)),
          ...(escalation?.customEvents || []).filter((e) => e.checked).map((e) => e.key),
        ]
      : coachingConfig?.escalationEventTypes || [];

    const payload: CoachingConfig = {
      ...threshold.thresholdForm.getRawValue(),
      ...auto.autoCoachingThresholdForm.getRawValue(),
      ...(escalation ? escalation.inPersonEscalationThresholdForm.getRawValue() : {}),
      eventTypes: selectedEvents,
      escalationEventTypes: escalationEvents,
      enableSelfCoaching: this.enableSelfCoaching,
    };
    this.loader = true;
    this.onSubmit(payload);
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
